<script setup lang="ts">
import { computed, ref } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import { Menu, X } from 'lucide';
import { MorphIcon } from 'morphicons/vue';
import BrandLogo from '@/components/marketing/shared/BrandLogo.vue';
import { Button } from '@/components/ui/button';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';
import { morphProps } from '@/lib/motion';
import { home } from '@/routes';

const navigation = [
    { label: 'Features', href: '#features' },
    { label: 'How it works', href: '#how-it-works' },
    { label: 'Video Studio', href: '#video-studio' },
    { label: 'FAQ', href: '#faq' },
];

const page = usePage();

/** Lifted so the trigger's menu mark can morph into a close mark. */
const menuOpen = ref(false);

const dashboardUrl = computed(() => page.props.dashboardUrl);
</script>

<template>
    <header
        class="bg-background/85 border-border sticky top-0 z-40 border-b backdrop-blur"
    >
        <div
            class="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8"
        >
            <Link :href="home()" class="shrink-0">
                <BrandLogo height="h-7 sm:h-8" />
                <span class="sr-only">LaunchStation home</span>
            </Link>

            <nav
                class="text-muted-foreground hidden items-center gap-6 text-sm lg:flex"
                aria-label="Main"
            >
                <a
                    v-for="item in navigation"
                    :key="item.href"
                    :href="home().url + item.href"
                    class="hover:text-foreground transition-colors"
                >
                    {{ item.label }}
                </a>
            </nav>

            <div class="flex items-center gap-2">
                <a
                    v-if="page.props.auth.user"
                    :href="dashboardUrl"
                    class="hover:text-foreground text-muted-foreground hidden text-sm sm:inline-flex"
                >
                    Dashboard
                </a>
                <a
                    v-else
                    :href="dashboardUrl + '/sign-in'"
                    class="hover:text-foreground text-muted-foreground hidden text-sm sm:inline-flex"
                >
                    Sign in
                </a>

                <Button as-child size="lg" class="hidden sm:inline-flex">
                    <a :href="home().url + '#early-access'"
                        >Join early access</a
                    >
                </Button>

                <Sheet v-model:open="menuOpen">
                    <SheetTrigger as-child>
                        <Button
                            variant="ghost"
                            size="icon-lg"
                            class="lg:hidden"
                        >
                            <MorphIcon
                                v-bind="morphProps"
                                :icon="menuOpen ? X : Menu"
                                class="size-5"
                                aria-hidden="true"
                            />
                            <span class="sr-only">
                                {{ menuOpen ? 'Close menu' : 'Open menu' }}
                            </span>
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="right" class="w-72">
                        <SheetHeader>
                            <SheetTitle>Menu</SheetTitle>
                        </SheetHeader>

                        <nav class="grid gap-1 px-4" aria-label="Mobile">
                            <SheetClose
                                v-for="item in navigation"
                                :key="item.href"
                                as-child
                            >
                                <a
                                    :href="home().url + item.href"
                                    class="hover:bg-accent rounded-lg px-3 py-2 text-sm"
                                >
                                    {{ item.label }}
                                </a>
                            </SheetClose>
                        </nav>

                        <div class="mt-2 grid gap-2 px-4">
                            <Button as-child size="lg">
                                <a :href="home().url + '#early-access'"
                                    >Join early access</a
                                >
                            </Button>
                            <a
                                v-if="page.props.auth.user"
                                :href="dashboardUrl"
                                class="hover:bg-accent rounded-lg px-3 py-2 text-center text-sm"
                            >
                                Dashboard
                            </a>
                            <a
                                v-else
                                :href="dashboardUrl + '/sign-in'"
                                class="hover:bg-accent rounded-lg px-3 py-2 text-center text-sm"
                            >
                                Sign in
                            </a>
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </div>
    </header>
</template>
